'use client'

import "./block-host-reset.css";
import "./layout.css";

import { lazy } from 'react'
import '@/lib/notebook/Blocks/shared/composition/composition.css'
import { getBlockComponent, getResolvedBlockMeta } from '@/lib/notebook/registry/block-components'
import { blockHasSemanticPaste, usePaste } from '@/lib/notebook/core/usePaste'
import { LazyBlockSurface } from '@/lib/notebook/Blocks/shared/LazyBlockSurface'
import {
  BlockPresentationProvider,
} from './composition'
import { BLOCK_PRESENTATION } from './composition/constants'

const TabsWrapperLazy = lazy(() => import('../tabs/TabsWrapper'))

const TAILWIND_BLOCK_COMPONENTS = new Set([
  'SummaryBlock',
  'InsightBlock',
  'QuestionBlock',
  'ReferenceBlock',
  'TimelineBlock',
])

function BlockHostInner({ sectionId, type, data, onChange, locked, structural }) {
  const { resolved, entry } = getResolvedBlockMeta(type)
  const Comp = getBlockComponent(type)
  const safeOnChange = locked ? undefined : onChange
  const canPaste =
    Boolean(safeOnChange) && !locked && !structural && blockHasSemanticPaste(resolved)
  const { onPasteCapture } = usePaste(
    resolved,
    sectionId,
    data ?? {},
    safeOnChange,
    canPaste,
  )
  const hostProps = {
    'data-block-type': resolved,
    'data-section-id': sectionId,
    onPasteCapture: canPaste ? onPasteCapture : undefined,
  }

  if (!entry || !Comp) {
    return (
      <div className="bible nb-block-host nb-block-host--missing" role="alert" {...hostProps}>
        Unknown block type: {String(type)}
      </div>
    )
  }

  if (entry.component === 'Hero') {
    const props = { data: data ?? {}, onChange: safeOnChange, locked: locked ?? false, sectionId }
    return (
      <div className="bible nb-block-host" {...hostProps}>
        <LazyBlockSurface hostClassName="bible nb-block-host">
          <Comp {...props} />
        </LazyBlockSurface>
      </div>
    )
  }

  if (entry.component === 'Tabs') {
    return (
      <div className="bible nb-block-host nb-block-host--tabs" {...hostProps}>
        <LazyBlockSurface hostClassName="bible nb-block-host nb-block-host--tabs">
          <TabsWrapperLazy
            sectionId={sectionId}
            data={data}
            onChange={safeOnChange}
            locked={locked}
            structural={structural}
          />
        </LazyBlockSurface>
      </div>
    )
  }

  if (entry.component === 'RichText') {
    return (
      <div className="bible nb-block-host" {...hostProps}>
        <LazyBlockSurface hostClassName="bible nb-block-host">
          <Comp
            data={data}
            onChange={safeOnChange}
            locked={locked}
            sectionId={sectionId}
          />
        </LazyBlockSurface>
      </div>
    )
  }

  const props = {
    data: data ?? {},
    onChange: safeOnChange,
    locked,
    sectionId,
  }

  const isTw = TAILWIND_BLOCK_COMPONENTS.has(entry.component)
  const presentation =
    entry.presentation === BLOCK_PRESENTATION.EMBEDDED
      ? BLOCK_PRESENTATION.EMBEDDED
      : BLOCK_PRESENTATION.STANDALONE
  const hostClassName = isTw ? 'nb-block-host nb-block-host--tw' : 'bible nb-block-host'

  return (
    <div className={hostClassName} {...hostProps}>
      <BlockPresentationProvider mode={presentation}>
        <LazyBlockSurface hostClassName={hostClassName}>
          {isTw ? (
            <div className="nb-tw-block">
              <Comp {...props} />
            </div>
          ) : (
            <Comp {...props} />
          )}
        </LazyBlockSurface>
      </BlockPresentationProvider>
    </div>
  )
}

export default BlockHostInner
